import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import NoPropertyImage from '@propertypal/shared/src/resources/fallbacks/no-photo-icon-small.png';
import { getItem, setItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types/property';
import PropertyPrice from '@propertypal/web-ui/src/property/PropertyPrice';
import { MidHeading } from '@propertypal/web-ui/src/typography';
import React, { ReactEventHandler } from 'react';
import { useTheme } from 'styled-components';
import { Container, PropertyRow } from './RecentlyViewed.style';

export const STORAGE_KEY = 'recently-viewed-properties';
const MAX_RECENT_VIEWS = 5;

interface RecentProperty {
  id: number;
  image?: string;
  displayAddressLine1: string;
  displayAddressLine2: string;
  price: Property['price'];
  path: string;
}

export const addRecentProperty = (property: Property) => {
  const properties = getItem<RecentProperty[]>(STORAGE_KEY);

  // Some 404 pages return odd results, where the property is an empty object with only a null path
  if (property.path && property.published) {
    const recentyProperty: RecentProperty = {
      id: property.id,
      image: property.images?.length ? property.images[0].urls['580x425:FILL_CROP'] : undefined,
      displayAddressLine1: property.displayAddressLine1,
      displayAddressLine2: property.displayAddressLine2,
      price: property.price,
      path: property.path,
    };

    if (!properties) {
      setItem(STORAGE_KEY, [recentyProperty]);
    } else {
      const filteredProperties = properties.filter((p) => p.id !== property.id);
      const recentProperties = [recentyProperty, ...filteredProperties];
      setItem(STORAGE_KEY, recentProperties.slice(0, MAX_RECENT_VIEWS));
    }
  }
};

const RecentlyViewed = () => {
  const [properties] = useLocalStorage<RecentProperty[]>(STORAGE_KEY, []);
  const theme = useTheme();

  const handleImageError: ReactEventHandler<HTMLImageElement> = (e) => {
    // @ts-ignore
    e.target.src = NoPropertyImage.src;
  };

  if (!properties?.length) {
    return null;
  }

  return (
    <Container>
      <MidHeading mb={15}>Recently Viewed</MidHeading>

      {properties.map((property) => (
        <PropertyRow href={property.path} key={property.id}>
          <img
            src={property.image || NoPropertyImage.src}
            alt={property.displayAddressLine1}
            onError={handleImageError}
          />

          <div>
            <p>
              <b>{property.displayAddressLine1}</b>, {property.displayAddressLine2}
            </p>
            <PropertyPrice
              containerStyle={{ marginTop: 8 }}
              price={property.price}
              fontSize={16}
              primaryColor={theme.textDark}
            />
          </div>
        </PropertyRow>
      ))}
    </Container>
  );
};

export default RecentlyViewed;
